import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import api from "../../services/api";
import Template from "../../template";
import MiniaturaFoto from "../../components/FotoUsuario/Miniatura";
import * as S from "./styled";

/*
  Tela da gestão para acompanhar o aviso de entrega atrasada ao responsável.

  Backend:
    GET  /avisos-entrega/previa     — quem receberia email na próxima execução
    GET  /avisos-entrega/historico  — o que já foi avisado (com filtros e paginação)
    POST /avisos-entrega/disparar   — roda o envio na hora (o normal é o job diário)

  Duas abas porque são duas perguntas diferentes: "quem está devendo agora" e
  "para quem já avisamos". A prévia não envia nem grava nada — é só leitura.
*/

const POR_PAGINA = 20;

type Tipo = "proximo" | "atrasado";

/* o que muda de um aviso para o outro; o resto da tela é igual */
const TEXTOS: Record<Tipo, {
  aba: string;
  icone: string;
  titulo: string;
  resumo: string;
  colunaPrazo: string;
  colunaAluno: string;
  vazioPrevia: string;
  explicacao: string;
}> = {
  proximo: {
    aba: "Prazo encerrando",
    icone: "fas fa-hourglass-half",
    titulo: "Lembrete de prazo",
    resumo:
      "Um dia antes do prazo, quem ainda não enviou a redação recebe um lembrete — o responsável e o próprio aluno.",
    colunaPrazo: "Vence amanhã",
    colunaAluno: "Aluno",
    vazioPrevia: "Nenhuma atividade vence amanhã sem envio.",
    explicacao:
      "Só entram atividades que vencem no dia seguinte.",
  },
  atrasado: {
    aba: "Não entregues",
    icone: "fas fa-triangle-exclamation",
    titulo: "Entrega atrasada",
    resumo:
      "Quando o prazo passa e o aluno não entregou, o responsável e o próprio aluno recebem um email — quem não tem responsável cadastrado é avisado direto.",
    colunaPrazo: "Prazo",
    colunaAluno: "Aluno que não entregou",
    vazioPrevia:
      "Nenhuma atividade vencida sem entrega, entre os alunos com email ou responsável.",
    explicacao:
      "Entram os prazos vencidos nos últimos dias (3, por padrão).",
  },
};

const FILTROS_VAZIOS = { aluno: "", responsavel: "", de: "", ate: "" };

/* aluno | atividade | prazo | responsável */
const COLUNAS_PREVIA = "minmax(0, 1.4fr) minmax(0, 2fr) 120px minmax(0, 1.8fr)";
/* as mesmas + quando foi avisado */
const COLUNAS_HISTORICO =
  "minmax(0, 1.3fr) minmax(0, 1.8fr) 110px minmax(0, 1.6fr) 150px";

interface Pendencia {
  atividade_id: number;
  entrega: string;
  tema: string | null;
  turma: string | null;
  aluno_id: number;
  aluno: string;
  aluno_email: string | null;
  aluno_foto: string | null;
  /* nulos quando o aluno não tem responsável cadastrado */
  id_responsavel: number | null;
  responsavel: string | null;
  email: string | null;
}

interface Aviso {
  id: number;
  aluno_id: number;
  aluno: string;
  aluno_foto: string | null;
  atividade_id: number;
  atividade: string | null;
  turma: string | null;
  entrega: string | null;
  responsavel: string | null;
  email: string;
  /* endereço do aluno, preenchido só quando a cópia para ele saiu */
  email_aluno: string | null;
  criado_em: string;
  enviado_em: string | null;
}

/* "2026-09-25" -> "25/09/2026", sem passar por Date (que desloca o dia pelo fuso). */
function dataBR(iso: string | null) {
  if (!iso) return "—";

  const [ano, mes, dia] = iso.slice(0, 10).split("-");

  return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
}

/* timestamp: aqui converter pelo fuso do navegador é o certo */
function momentoBR(valor: string | null) {
  if (!valor) return null;

  const d = new Date(valor);

  return isNaN(d.getTime())
    ? null
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function AvisosEntrega() {
  /* aba de cima: qual aviso estamos olhando; aba de baixo: a avisar ou já enviados */
  const [tipo, setTipo] = useState<Tipo>("atrasado");
  const [aba, setAba] = useState<"previa" | "historico">("previa");

  const [pendencias, setPendencias] = useState<Pendencia[]>([]);
  const [destinatarios, setDestinatarios] = useState(0);
  const [carregandoPrevia, setCarregandoPrevia] = useState(true);

  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalHistorico, setTotalHistorico] = useState(0);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);

  const [confirmando, setConfirmando] = useState(false);
  const [disparando, setDisparando] = useState(false);

  const carregarPrevia = useCallback(async () => {
    setCarregandoPrevia(true);

    try {
      const { data } = await api.get("/avisos-entrega/previa", { params: { tipo } });

      setPendencias(data.pendencias || []);
      setDestinatarios(data.destinatarios || 0);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Não foi possível carregar a prévia dos avisos."
      );
      setPendencias([]);
    } finally {
      setCarregandoPrevia(false);
    }
  }, [tipo]);

  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);

    try {
      const params: Record<string, string> = {
        tipo,
        page: String(pagina),
        limit: String(POR_PAGINA),
      };

      /* campo vazio viraria filtro por string vazia */
      (["aluno", "responsavel", "de", "ate"] as const).forEach((campo) => {
        const valor = filtros[campo].trim();
        if (valor) params[campo] = valor;
      });

      const { data } = await api.get("/avisos-entrega/historico", { params });

      setAvisos(data.data || []);
      setTotalPaginas(data.paginacao?.totalPages || 1);
      setTotalHistorico(data.paginacao?.totalCount || 0);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Não foi possível carregar o histórico de avisos."
      );
      setAvisos([]);
    } finally {
      setCarregandoHistorico(false);
    }
  }, [pagina, filtros, tipo]);

  useEffect(() => {
    carregarPrevia();
  }, [carregarPrevia]);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  function alterarFiltro(campo: keyof typeof FILTROS_VAZIOS, valor: string) {
    setPagina(1);
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  }

  async function dispararAgora() {
    setDisparando(true);

    try {
      const { data } = await api.post("/avisos-entrega/disparar", null, { params: { tipo } });

      if (data.emailsEnviados > 0) {
        toast.success(
          `${data.emailsEnviados} email(s) enviado(s) — ${data.emailsAlunos} para alunos —, ` +
            `cobrindo ${data.pendencias} pendência(s).`
        );
      } else if (data.emailsComFalha === 0) {
        /*
          "Nenhum email a enviar" só vale quando não havia ninguém pendente. Com
          falhas, dizer isso junto do aviso de erro é contraditório — foi o que
          apareceu na tela quando o provedor recusou um lote inteiro.
        */
        toast.info("Nenhum email a enviar: ninguém pendente dentro da janela.");
      }

      if (data.emailsComFalha > 0) {
        toast.warn(
          `${data.emailsComFalha} envio(s) falharam; as pendências seguem abertas e ` +
            `serão tentadas de novo. Veja o motivo no log do servidor.`
        );
      }

      setConfirmando(false);
      setPagina(1);
      await Promise.all([carregarPrevia(), carregarHistorico()]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Não foi possível disparar os avisos agora."
      );
    } finally {
      setDisparando(false);
    }
  }

  const filtroAtivo = Object.values(filtros).some((v) => v.trim() !== "");

  /* o aluno recebe uma cópia própria; sem email cadastrado, só o responsável recebe */
  const alunosComEmail = new Set(
    pendencias.filter((p) => p.aluno_email).map((p) => p.aluno_id)
  ).size;

  return (
    <Template>
      <S.Pagina>
        <S.Cabecalho>
          <S.Identidade>
            <S.IconeTitulo>
              <i className={TEXTOS[tipo].icone} />
            </S.IconeTitulo>
            <div>
              <h1>Avisos de prazo · {TEXTOS[tipo].titulo}</h1>
              <p>
                {TEXTOS[tipo].resumo} Aqui você vê <strong>quem seria avisado</strong>{" "}
                agora e <strong>para quem já avisamos</strong>.
              </p>
            </div>
          </S.Identidade>

          <S.Contadores>
            <S.Contador $cor="#a35b00" $fundo="#fff8ec" $borda="#ffd8a8">
              <strong>{carregandoPrevia ? "—" : pendencias.length}</strong>
              <span>A avisar</span>
            </S.Contador>
            <S.Contador $cor="#0b5a92" $fundo="#eef6fd" $borda="#c5e0f7">
              <strong>{carregandoPrevia ? "—" : destinatarios + alunosComEmail}</strong>
              <span>Emails a enviar</span>
            </S.Contador>
            <S.Contador $cor="#1b7a4b" $fundo="#eafaf1" $borda="#b7e4c7">
              <strong>{carregandoHistorico ? "—" : totalHistorico}</strong>
              <span>Já avisados</span>
            </S.Contador>
          </S.Contadores>
        </S.Cabecalho>

        {/*
          Dois níveis de aba: em cima o tipo de aviso (são dois emails diferentes,
          com listas independentes), embaixo o estado — a avisar ou já enviados.
        */}
        <S.Abas>
          {(["proximo", "atrasado"] as Tipo[]).map((t) => (
            <S.AbaTipo
              key={t}
              $ativa={tipo === t}
              onClick={() => {
                if (t === tipo) return;
                setTipo(t);
                setPagina(1);
              }}
            >
              <i className={TEXTOS[t].icone} />
              {TEXTOS[t].aba}
            </S.AbaTipo>
          ))}
        </S.Abas>

        <S.Abas>
          <S.Aba $ativa={aba === "previa"} onClick={() => setAba("previa")}>
            <i className="fas fa-clock" />
            A avisar ({carregandoPrevia ? "…" : pendencias.length})
          </S.Aba>
          <S.Aba $ativa={aba === "historico"} onClick={() => setAba("historico")}>
            <i className="fas fa-paper-plane" />
            Já enviados ({carregandoHistorico ? "…" : totalHistorico})
          </S.Aba>
        </S.Abas>

        {aba === "previa" ? (
          <>
            <S.Aviso>
              <i className="fas fa-info-circle" />
              <div>
                Esta lista é só uma <strong>prévia</strong>: nada é enviado ao abrir a
                tela. O envio acontece sozinho, uma vez por dia as 8h da manhã.
                Cada aluno recebe <strong>um aviso por atividade</strong>, e
                quem tem várias pendências recebe um email só, com tudo. Vai para o
                responsável e, em mensagem separada, para o aluno. Caso não haja
                responsável cadastrado, o email vai só para o aluno. {TEXTOS[tipo].explicacao}
              </div>
            </S.Aviso>

            <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
              <S.BotaoSecundario onClick={carregarPrevia} disabled={carregandoPrevia}>
                <i className="fas fa-rotate" />
                Atualizar
              </S.BotaoSecundario>
              <S.BotaoPrincipal
                onClick={() => setConfirmando(true)}
                disabled={carregandoPrevia || pendencias.length === 0}
                title={
                  pendencias.length === 0
                    ? "Não há ninguém a avisar no momento"
                    : "Enviar os avisos agora, sem esperar o horário"
                }
              >
                <i className="fas fa-paper-plane" />
                Enviar agora
              </S.BotaoPrincipal>
            </div>

            <S.Tabela>
              <S.Titulos $colunas={COLUNAS_PREVIA}>
                <span>Aluno</span>
                <span>Atividade</span>
                <span>{TEXTOS[tipo].colunaPrazo}</span>
                <span>Responsável que será avisado</span>
              </S.Titulos>

              {carregandoPrevia ? (
                <S.Carregando>Carregando a prévia…</S.Carregando>
              ) : pendencias.length === 0 ? (
                <S.Vazio>
                  <i className="fas fa-circle-check" />
                  <h3>Ninguém a avisar</h3>
                  <p>{TEXTOS[tipo].vazioPrevia}</p>
                </S.Vazio>
              ) : (
                pendencias.map((p) => (
                  <S.Linha key={`${p.aluno_id}-${p.atividade_id}`} $colunas={COLUNAS_PREVIA}>
                    <S.Celula>
                      <span className="rotulo">Aluno</span>
                      <div className="d-flex align-items-center" style={{ gap: 9 }}>
                        <MiniaturaFoto foto={p.aluno_foto} nome={p.aluno} tamanho={34} />
                        <div style={{ minWidth: 0 }}>
                          <span className="principal">{p.aluno}</span>
                          <span className="apoio">
                            {p.aluno_email || "sem email — só o responsável recebe"}
                          </span>
                        </div>
                      </div>
                    </S.Celula>

                    <S.Celula>
                      <span className="rotulo">Atividade</span>
                      <span className="principal">{p.tema || "Atividade sem título"}</span>
                      <span className="apoio">{p.turma || "Sem turma"}</span>
                    </S.Celula>

                    <S.Celula>
                      <span className="rotulo">Prazo</span>
                      <S.Selo $tom="alerta">{dataBR(p.entrega)}</S.Selo>
                    </S.Celula>

                    <S.Celula>
                      <span className="rotulo">Responsável</span>
                      {p.email ? (
                        <>
                          <span className="principal">{p.responsavel}</span>
                          <span className="apoio">{p.email}</span>
                        </>
                      ) : (
                        /* sem responsável: o aviso vai só para o aluno */
                        <S.Selo $tom="neutro">Sem responsável · avisa o aluno</S.Selo>
                      )}
                    </S.Celula>
                  </S.Linha>
                ))
              )}
            </S.Tabela>
          </>
        ) : (
          <>
            <S.Filtros>
              <S.Campo>
                <label>Aluno</label>
                <div className="entrada">
                  <i className="fas fa-user-graduate" />
                  <input
                    placeholder="Nome do aluno"
                    value={filtros.aluno}
                    onChange={(e) => alterarFiltro("aluno", e.target.value)}
                  />
                </div>
              </S.Campo>

              <S.Campo>
                <label>Responsável</label>
                <div className="entrada">
                  <i className="fas fa-user-shield" />
                  <input
                    placeholder="Nome ou email"
                    value={filtros.responsavel}
                    onChange={(e) => alterarFiltro("responsavel", e.target.value)}
                  />
                </div>
              </S.Campo>

              <S.Campo>
                <label>Avisado de</label>
                <div className="entrada">
                  <i className="fas fa-calendar-day" />
                  <input
                    type="date"
                    value={filtros.de}
                    onChange={(e) => alterarFiltro("de", e.target.value)}
                  />
                </div>
              </S.Campo>

              <S.Campo>
                <label>Avisado até</label>
                <div className="entrada">
                  <i className="fas fa-calendar-check" />
                  <input
                    type="date"
                    value={filtros.ate}
                    onChange={(e) => alterarFiltro("ate", e.target.value)}
                  />
                </div>
              </S.Campo>

              {filtroAtivo && (
                <S.BotaoLimpar
                  onClick={() => {
                    setPagina(1);
                    setFiltros(FILTROS_VAZIOS);
                  }}
                >
                  <i className="fas fa-xmark" />
                  Limpar filtros
                </S.BotaoLimpar>
              )}
            </S.Filtros>

            <S.Tabela>
              <S.Titulos $colunas={COLUNAS_HISTORICO}>
                <span>{TEXTOS[tipo].colunaAluno}</span>
                <span>Atividade</span>
                <span>Prazo</span>
                <span>Quem foi avisado</span>
                <span>Aviso</span>
              </S.Titulos>

              {carregandoHistorico ? (
                <S.Carregando>Carregando o histórico…</S.Carregando>
              ) : avisos.length === 0 ? (
                <S.Vazio>
                  <i className="fas fa-inbox" />
                  <h3>
                    Nenhum aviso de {TEXTOS[tipo].aba.toLowerCase()}{" "}
                    {filtroAtivo ? "com esses filtros" : "enviado ainda"}
                  </h3>
                  <p>
                    {filtroAtivo
                      ? "Tente ampliar o período ou limpar os filtros."
                      : "Assim que um prazo vencer sem entrega, o aviso aparece aqui."}
                  </p>
                </S.Vazio>
              ) : (
                avisos.map((a) => {
                  const enviado = momentoBR(a.enviado_em);

                  return (
                    <S.Linha key={a.id} $colunas={COLUNAS_HISTORICO}>
                      <S.Celula>
                        <span className="rotulo">Aluno</span>
                        <div className="d-flex align-items-center" style={{ gap: 9 }}>
                          <MiniaturaFoto foto={a.aluno_foto} nome={a.aluno} tamanho={34} />
                          <div style={{ minWidth: 0 }}>
                            <span className="principal">{a.aluno}</span>
                            <span className="apoio">ID: {a.aluno_id}</span>
                          </div>
                        </div>
                      </S.Celula>

                      <S.Celula>
                        <span className="rotulo">Atividade</span>
                        <span className="principal">
                          {a.atividade || "Atividade removida da turma"}
                        </span>
                        <span className="apoio">{a.turma || "Sem turma"}</span>
                      </S.Celula>

                      <S.Celula>
                        <span className="rotulo">Prazo</span>
                        <S.Selo $tom="neutro">{dataBR(a.entrega)}</S.Selo>
                      </S.Celula>

                      <S.Celula>
                        <span className="rotulo">Avisado</span>
                        {a.responsavel ? (
                          <>
                            <span className="principal">{a.responsavel}</span>
                            <span className="apoio">{a.email}</span>
                            {/*
                              O aluno recebe uma mensagem própria. Quando não tem
                              email cadastrado, só o responsável é avisado — e a
                              coluna precisa deixar isso explícito.
                            */}
                            <span className="apoio" style={{ marginTop: 3 }}>
                              {a.email_aluno ? (
                                <>
                                  <i className="fas fa-user-graduate" style={{ marginRight: 5 }} />
                                  e o aluno: {a.email_aluno}
                                </>
                              ) : (
                                <em>aluno não avisado (sem email)</em>
                              )}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="principal">O próprio aluno</span>
                            <span className="apoio">{a.email}</span>
                            <span className="apoio" style={{ marginTop: 3 }}>
                              <em>sem responsável cadastrado</em>
                            </span>
                          </>
                        )}
                      </S.Celula>

                      <S.Celula>
                        <span className="rotulo">Aviso</span>
                        {enviado ? (
                          <>
                            <S.Selo $tom="ok">
                              <i className="fas fa-check" />
                              Enviado
                            </S.Selo>
                            <span className="apoio" style={{ marginTop: 4 }}>
                              {enviado}
                            </span>
                          </>
                        ) : (
                          /*
                            Reserva gravada sem confirmação de envio: só acontece se o
                            processo cair no meio. Aparece aqui para não virar silêncio.
                          */
                          <S.Selo $tom="alerta" title="O envio não chegou a ser confirmado">
                            <i className="fas fa-triangle-exclamation" />
                            Não confirmado
                          </S.Selo>
                        )}
                      </S.Celula>
                    </S.Linha>
                  );
                })
              )}

              {!carregandoHistorico && avisos.length > 0 && (
                <S.Paginacao>
                  <span className="contagem">
                    Página {pagina} de {totalPaginas} · {totalHistorico} aviso(s)
                  </span>
                  <div className="botoes">
                    <button disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                      <i className="fas fa-chevron-left" /> Anterior
                    </button>
                    <button
                      disabled={pagina >= totalPaginas}
                      onClick={() => setPagina((p) => p + 1)}
                    >
                      Próxima <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </S.Paginacao>
              )}
            </S.Tabela>
          </>
        )}

        {confirmando && (
          <S.Overlay onClick={() => !disparando && setConfirmando(false)}>
            <S.Dialogo onClick={(e) => e.stopPropagation()}>
              <S.DialogoTopo>
                <i className="fas fa-triangle-exclamation" />
                Enviar os avisos agora?
              </S.DialogoTopo>
              <S.DialogoCorpo>
                <p>
                  Serão enviados <strong>{destinatarios + alunosComEmail} email(s)</strong> —{" "}
                  {destinatarios} a responsáveis e {alunosComEmail} a alunos —, cobrindo{" "}
                  <strong>{pendencias.length} pendência(s)</strong>.
                </p>
                <p style={{ marginBottom: 0 }}>
                  Email enviado não tem volta, e cada aluno só é avisado uma vez por
                  atividade — depois disso, estas pendências não voltam a gerar aviso.
                </p>
              </S.DialogoCorpo>
              <S.DialogoAcoes>
                <S.BotaoLimpar onClick={() => setConfirmando(false)} disabled={disparando}>
                  Cancelar
                </S.BotaoLimpar>
                <S.BotaoPrincipal onClick={dispararAgora} disabled={disparando}>
                  {disparando ? "Enviando…" : "Confirmar envio"}
                </S.BotaoPrincipal>
              </S.DialogoAcoes>
            </S.Dialogo>
          </S.Overlay>
        )}
      </S.Pagina>
    </Template>
  );
}

export default AvisosEntrega;
